import { isToday } from 'date-fns'
import { useMemo, type ReactNode } from 'react'
import { useGemButtonClearance } from '@opallabs/filters'
import { Button, Icon } from '@opallabs/lapidary'
import {
  indexItemsById,
  resolveItemColor,
  type ResolveChosenColor,
} from '../../resolveItemColor'
import {
  type CalendarItem,
  type CalendarItemKind,
  type ResolveDisplay,
  type ResolveImageUrl,
} from '../../types'
import { type CalendarPanelFormatters } from '../formatters'
import {
  buildDayItems,
  buildDaySummary,
  buildOngoingEntries,
} from '../panelData'
import { CalendarContentRow, CalendarMomentRow } from '../rows'
import * as styles from '../styles'
import { type CalendarPanelStep } from '../useCalendarPanel'

export type DayPanelProps = Readonly<{
  /** The day this panel is about. */
  date: Date
  formatters: CalendarPanelFormatters
  /**
   * Everything in view. The panel works out what belongs to the day, so the
   * caller never has to keep two lists in step.
   */
  items: readonly CalendarItem[]
  onClose: () => void
  onItemOpen: (id: string, options: { back: CalendarPanelStep }) => void
  renderChannelIcon?: (item: CalendarItem) => ReactNode
  /**
   * Draws the way to make something on this day. The panel gives it the footer
   * and nothing else, so the form and whether the day allows one at all stay
   * with the host.
   */
  renderCreate?: (date: Date) => ReactNode
  /** The workflow mark the grid draws on the same item's chip. */
  renderStatusIcon?: (item: CalendarItem, size: number) => ReactNode
  /** Matches the grid, so an item keeps its colour when the panel opens. */
  resolveChosenColor?: ResolveChosenColor
  resolveDisplay?: ResolveDisplay
  /**
   * Answers each moment row's tile. Only the host can fetch a picture; without
   * one, every moment is drawn as a placeholder.
   */
  resolveImageUrl?: ResolveImageUrl
  /** Matches the grid, so a turned-off in-market bar has no row here either. */
  shouldShowInMarketBars?: boolean
  /**
   * The kinds the grid draws. The summary leaves out the count of a kind that
   * is turned off, which would always be 0.
   */
  shownKinds?: readonly CalendarItemKind[]
}>

const ALL_KINDS: readonly CalendarItemKind[] = ['moment', 'content']

/**
 * Everything happening on one day: what it holds in a line, then the runs of
 * days passing through it, then the moments and content scheduled on it.
 */
export const DayPanel: React.FC<DayPanelProps> = ({
  date,
  formatters,
  items,
  onClose,
  onItemOpen,
  renderChannelIcon,
  renderCreate,
  renderStatusIcon,
  resolveChosenColor,
  resolveDisplay,
  resolveImageUrl,
  shouldShowInMarketBars,
  shownKinds = ALL_KINDS,
}) => {
  const gemButtonClearance = useGemButtonClearance()
  const itemsById = useMemo(() => indexItemsById(items), [items])

  const ongoing = useMemo(
    () => buildOngoingEntries(items, date, { shouldShowInMarketBars }),
    [date, items, shouldShowInMarketBars]
  )
  const dayItems = useMemo(() => buildDayItems(items, date), [date, items])

  // An item can be both live through the day and scheduled on it, and the panel
  // lists it twice on purpose. The summary counts it once, because a person
  // reading "7 Moments, 12 Content" is asking how much there is, not how many
  // rows there are.
  const summary = useMemo(() => {
    const listed = new Map<string, CalendarItem>()
    ongoing.forEach((entry) => listed.set(entry.item.id, entry.item))
    dayItems.forEach((item) => listed.set(item.id, item))

    return buildDaySummary(Array.from(listed.values()), { resolveDisplay })
  }, [dayItems, ongoing, resolveDisplay])

  const openItem = (item: CalendarItem) =>
    onItemOpen(item.id, { back: { type: 'day', date } })

  const renderRow = (
    item: CalendarItem,
    { key, range }: { key: string; range?: string }
  ) => {
    const color = resolveItemColor(item, itemsById, resolveChosenColor)

    if (item.kind === 'moment') {
      return (
        <CalendarMomentRow
          key={key}
          color={color}
          imageUrl={resolveImageUrl?.(item)}
          item={item}
          range={range}
          renderStatusIcon={renderStatusIcon}
          onOpen={openItem}
        />
      )
    }

    return (
      <CalendarContentRow
        key={key}
        accountName={
          item.accountId
            ? resolveDisplay?.('account', item.accountId)
            : undefined
        }
        color={color}
        item={item}
        momentTitle={
          item.parentId
            ? (itemsById.get(item.parentId)?.title ??
              resolveDisplay?.('moment', item.parentId))
            : undefined
        }
        range={range}
        renderChannelIcon={renderChannelIcon}
        renderStatusIcon={renderStatusIcon}
        onOpen={openItem}
      />
    )
  }

  return (
    <div className={styles.selectors.panel}>
      <div className={styles.selectors.header}>
        <div className={styles.selectors.headerTop}>
          <span className={styles.selectors.title}>
            {formatters.longDate(date)}
          </span>
          <Button
            ariaLabel="Close"
            icon={<Icon ariaHidden={true} glyph="gem-close" />}
            name="close-day-panel"
            variant="ghost"
            onClick={onClose}
          />
        </div>

        <section
          aria-label="What this day holds"
          className={styles.selectors.summary}
        >
          <div className={styles.selectors.summaryHeadline}>
            {[
              shownKinds.includes('moment') &&
                `${summary.momentCount} Moment${summary.momentCount === 1 ? '' : 's'}`,
              shownKinds.includes('content') &&
                `${summary.contentCount} Content`,
            ]
              .filter(Boolean)
              .join(', ')}
          </div>
          {summary.channels.map((channel) => (
            <div key={channel.id} className={styles.selectors.summaryChannel}>
              {renderChannelIcon?.(channel.item)}
              <span className={styles.selectors.truncate}>{channel.name}</span>
              <span className={styles.selectors.summaryCount}>
                {`${channel.count} (${channel.share}%)`}
              </span>
            </div>
          ))}
          {summary.hiddenChannelCount > 0 && (
            <div className={styles.selectors.summaryMore}>
              {`${summary.hiddenChannelCount} more channel${summary.hiddenChannelCount === 1 ? '' : 's'}`}
            </div>
          )}
        </section>
      </div>

      <div className={styles.selectors.body}>
        {ongoing.length > 0 && (
          <>
            <div className={styles.selectors.subheading}>Ongoing</div>
            {ongoing.map((entry) =>
              renderRow(entry.item, {
                key: entry.key,
                range: `${formatters.shortDate(entry.start)} – ${formatters.shortDate(entry.end)}`,
              })
            )}
          </>
        )}

        {dayItems.length > 0 && (
          <>
            <div className={styles.selectors.subheading}>
              {isToday(date) ? 'Today' : 'Scheduled'}
            </div>
            {dayItems.map((item) => renderRow(item, { key: item.id }))}
          </>
        )}
      </div>

      {renderCreate && (
        <div
          className={styles.selectors.footer}
          style={{ paddingInlineEnd: gemButtonClearance }}
        >
          {renderCreate(date)}
        </div>
      )}
    </div>
  )
}
DayPanel.displayName = 'Calendar: DayPanel'
